import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isSmsCapable } from "@/lib/phone-format";
import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";
import type { MeetingLocationType } from "@/lib/db-types";

export interface BookContact {
  id: string;
  full_name: string;
  /** Med når kilden har den, så redigering ikke tømmer tittelen. */
  title?: string | null;
  email: string | null;
  phone: string | null;
}

interface BookMeetingBasicsFieldsProps {
  contacts: BookContact[];
  contact: BookContact | null;
  contactId: string;
  onContact: (id: string) => void;
  /** Ny kontakt og redigering av den valgte, rett fra dialogen. Utelatt når brukeren ikke kan skrive kontakter. */
  onNewContact?: () => void;
  onEditContact?: () => void;
  title: string;
  onTitle: (v: string) => void;
  locationType: MeetingLocationType;
  onLocationType: (v: MeetingLocationType) => void;
  address: string;
  onAddress: (v: string) => void;
  duration: number;
  onDuration: (v: number) => void;
}

/** Trinn 1 i bookingdialogen: kontaktperson, tittel, møteform og varighet. */
export default function BookMeetingBasicsFields(p: BookMeetingBasicsFieldsProps) {
  const { contact } = p;
  return (
  <section aria-label="Hvem og hva" className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="book-contact">Kontaktperson</Label>
              <Select value={p.contactId} onValueChange={p.onContact}>
                <SelectTrigger id="book-contact" aria-label="Kontaktperson">
                  <SelectValue placeholder="Velg kontaktperson" />
                </SelectTrigger>
                <SelectContent>
                  {p.contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contact && !contact.email && (
                <p className="text-xs text-destructive">
                  Kontaktpersonen mangler e-post. {p.onEditContact ? "Rediger kontakten og legg den til." : "Legg den til på kundekortet først."}
                </p>
              )}
              {contact && contact.email && !isSmsCapable(contact.phone) && (
                <p className={cn("text-xs", TONE_TEXT.near)}>SMS-påminnelser hoppes over: kontakten har ikke et mobilnummer vi kan sende til.</p>
              )}
              {p.contacts.length === 0 && <p className="text-xs text-destructive">Ingen kontaktpersoner. Legg til en kontakt med e-post først.</p>}
              {p.onNewContact && (
                <div className="flex gap-3 text-sm">
                  <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={p.onNewContact}>
                    Ny kontaktperson
                  </button>
                  {contact && p.onEditContact && (
                    <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={p.onEditContact}>
                      Rediger kontakt
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="book-title">Tittel</Label>
              <Input id="book-title" value={p.title} onChange={(e) => p.onTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="book-location">Møteform</Label>
              <Select value={p.locationType} onValueChange={(v) => p.onLocationType(v as MeetingLocationType)}>
                <SelectTrigger id="book-location" aria-label="Møteform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="physical">Fysisk</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="book-duration">Varighet</Label>
              <Select value={String(p.duration)} onValueChange={(v) => p.onDuration(Number(v))}>
                <SelectTrigger id="book-duration" aria-label="Varighet">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[30, 45, 60].map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} minutter
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {p.locationType === "physical" && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="book-address">Adresse</Label>
                <Input id="book-address" value={p.address} onChange={(e) => p.onAddress(e.target.value)} />
              </div>
            )}
          </section>
  );
}
